import { useMemo } from "react";
import { useApiConfig, installServerErrorInterceptor, SERVER_ERROR_MESSAGE } from "common-fe";
import {
  Configuration,
  ShowsApi,
  ClassesApi,
  RidesApi,
  ScoreSheetsApi,
  TestsApi,
  PeopleApi,
  HorsesApi,
  ResultsApi,
  JudgesApi,
  DemoApi,
  CatalogApi,
  RingStateApi,
  InstanceApi,
  PresenceApi,
} from "score-common";

// Single place that turns the current auth session into instantiated
// score-common API clients (mirrors scribe-fe/src/api/scoreApi.js). `useApiConfig`
// (common-fe) stamps the Cognito bearer token AND the active-org `X-Org-Id`
// header (from the mounted ActiveOrgProvider) onto every request. Base URL comes
// from VITE_SCORE_API_BASE_URL.
const SCORE_API_BASE_URL = import.meta.env.VITE_SCORE_API_BASE_URL;

// Rewrite every failed API call's error.message to a generic "Server Error"
// (raw detail kept on the error for console logging) so backend codes never
// reach the UI. Installed once on the shared axios singleton at module load.
installServerErrorInterceptor();

export function useScoreApi() {
  const buildConfig = useApiConfig();
  return useMemo(() => {
    const config = new Configuration(buildConfig(SCORE_API_BASE_URL));
    return {
      shows: new ShowsApi(config),
      classes: new ClassesApi(config),
      rides: new RidesApi(config),
      scoreSheets: new ScoreSheetsApi(config),
      tests: new TestsApi(config),
      people: new PeopleApi(config),
      horses: new HorsesApi(config),
      results: new ResultsApi(config),
      judges: new JudgesApi(config),
      demo: new DemoApi(config),
      // card #1662 — GET /catalog, the global test-def catalog. Same
      // Configuration as everything else here even though the route itself
      // is org-agnostic (allowNoOrg, see apps/score-api/src/handlers/
      // catalog.js) -- an unused X-Org-Id header is harmless.
      catalog: new CatalogApi(config),
      // #1936 — GET list/single + PUT explicit set + DELETE clear, docs/score/ring-state.md.
      ringState: new RingStateApi(config),
      // #2373 — superuser foxbox instance config (list/write), consumes
      // #2372's routes. allowNoOrg on the API side; the unused X-Org-Id
      // header is harmless, same posture as `catalog` above.
      instance: new InstanceApi(config),
      // #2365 — GET /presence (#2363). Read-only here: scoreadmin never
      // heartbeats, that's scribe-fe's job (#2364).
      presence: new PresenceApi(config),
    };
  }, [buildConfig]);
}

// JSend unwrap: score-api wraps every success as { status: "success", data }.
export function unwrap(response) {
  const body = response?.data;
  if (!body || body.status !== "success") {
    // A 2xx response carrying a JSend failure envelope — don't leak the backend
    // message/code into the UI; log it and throw the generic message.
    // eslint-disable-next-line no-console
    console.error("[score-api]", body?.data?.message || body?.code, body);
    throw new Error(SERVER_ERROR_MESSAGE);
  }
  return body.data;
}
